/* eslint-disable react/self-closing-comp */
/* eslint-disable jsx-a11y/control-has-associated-label */
/* eslint-disable jsx-a11y/label-has-associated-control */
import classNames from "classnames";
import {
  ReactNode,
  RefObject,
  memo,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  RegisterOptions,
  useController,
  useFormContext,
} from "react-hook-form";
import EyeOpenIcon from "assets/icons/banger/eye-open.svg?react";
import EyeHideIcon from "assets/icons/banger/eye-unshown.svg?react";
import Locked from "assets/icons/glory/locked.svg?react";
import Edit from "assets/icons/glory/edit.svg?react";
import useClickOutside from "hooks/useClickOutside";
import Tooltip from "../Tooltip";

import "./styles.sass";

export type InputProps = {
  id?: string;
  disabled?: boolean;
  editable?: boolean;
  copied?: boolean;
  name: string;
  label?: string;
  placeholder?: string;
  error?: string;
  mode?: "light" | "dark";
  suffix?: string;
  help?: string;
  reset?: () => void;
  validation?: RegisterOptions;
  readOnly?: boolean;
  eyeClassName?: string;
  tooltipUp?: ReactNode;
  tooltipDown?: ReactNode;
  type?: "text" | "number" | "password" | "email" | "tel" | "search";
  inputMode?: "numeric" | "decimal";
  value: string;
  setError?: (field: string) => void;
  onBlur?: (value: string, props: InputProps) => void;
  onChange?: (value: string, props: InputProps) => void;
  onFocus?: (value: string, props: InputProps) => void;
  dataCy?: string;
};

const Input = (props: InputProps) => {
  const {
    disabled,
    editable = true,
    copied,
    name,
    label,
    error,
    mode = import.meta.env.VITE_APP_PROJECT_NAME === "banger"
      ? "dark"
      : "light",
    help,
    validation,
    placeholder,
    tooltipUp,
    tooltipDown,
    type = "text",
    inputMode,
    value = "",
    suffix,
    eyeClassName,
    onChange,
    setError,
    dataCy,
    id,
  } = props;

  const { control, setFocus } = useFormContext();

  const { field: fieldController } = useController({
    control,
    name,
    rules: validation,
    defaultValue: value,
  });
  const [inputType, setInputType] = useState(type);
  const [errorCaption, setErrorCaption] = useState<string>("");
  const [clearPressed, setClearPressed] = useState(false);
  const [isTooltipVisible, setIsTooltipVisible] = useState<boolean>(false);
  const tooltipTriggerRef: RefObject<HTMLButtonElement> =
    useRef<HTMLButtonElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const supportTextRef = useRef<HTMLDivElement | null>(null);

  const showSupportText = () => {
    if (supportTextRef.current) {
      supportTextRef.current.style.display = "block";
    }
  };

  const hideSupportText = () => {
    if (supportTextRef.current) {
      supportTextRef.current.style.display = "none";
    }
  };

  useEffect(() => {
    setErrorCaption(error ?? "");
  }, [error]);

  useEffect(() => {
    setInputType(type);
  }, [type]);

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (inputRef.current && supportTextRef.current) {
      inputRef.current.addEventListener("focus", showSupportText);
      inputRef.current.addEventListener("blur", hideSupportText);
      return () => {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        if (inputRef.current && supportTextRef.current) {
          inputRef.current.removeEventListener("focus", showSupportText);
          inputRef.current.removeEventListener("blur", hideSupportText);
        }
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputRef.current, supportTextRef.current]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!editable) {
      return;
    }

    setClearPressed(false);

    e.preventDefault();
    if (!disabled && onChange) {
      // eslint-disable-next-line prefer-destructuring, @typescript-eslint/no-shadow
      const value = e.target.value;
      fieldController.onChange(value);
      onChange(value, props);
    }
  };

  const handleReset = (e) => {
    e.preventDefault();
    fieldController.onChange("");
    if (onChange) {
      onChange("", props);
    }
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    setError(name);
    setClearPressed(true);
    setErrorCaption("");
    setFocus(name);
  };

  const handleShowPass = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (inputType === "password") {
      setInputType("text");
    } else {
      setInputType(type);
    }
    setFocus(name);
    // setTimeout(() => {
    //     inputRef.current?.setSelectionRange(fieldController.value.length, fieldController.value.length);
    // }, 1);
  };

  const handleCopy = (e) => {
    e.preventDefault();
    navigator.clipboard.writeText(fieldController.value);
  };

  const tooltipTrigger = (e) => {
    e.preventDefault();
    setIsTooltipVisible(!isTooltipVisible);
  };

  const tooltipClickOutsideHandler = (e) => {
    e.preventDefault();
    setIsTooltipVisible(false);
  };

  useClickOutside({
    refs: [tooltipRef, tooltipTriggerRef],
    handler: tooltipClickOutsideHandler,
  });

  const componentClass = classNames("Input", {
    "Input__with-error": !!errorCaption,
    [mode]: !!mode,
  });

  const nameClass = classNames("Input__name", {
    withValue: !!fieldController.value,
    error: !!errorCaption && !clearPressed,
    suffix: !!suffix,
    tooltip: !!tooltipUp || !!tooltipDown,
  });

  const inputClass = classNames("Input__input", {
    withValue: !!fieldController.value,
    noLabel: !label,
    dark: mode === "dark",
    disabled: !!disabled,
  });

  const borderClass = classNames("Input__border", {
    dark: mode === "dark",
    withValue: !!fieldController.value,
    error: !!errorCaption && !clearPressed,
    disabled: !!disabled,
  });

  const clearClass = classNames("Input__clear", {
    dark: mode === "dark",
    tooltip: !!tooltipUp || !!tooltipDown,
  });

  const eyeClass = classNames("Input__eye", eyeClassName, {
    moved: fieldController.value.length || !!errorCaption,
  });

  const suffixClass = classNames("Input__suffix", {
    error: !!errorCaption && !clearPressed,
  });

  const attentionClass = classNames("Input__attention", {
    suffix: !!suffix && !clearPressed,
    tooltip: !!tooltipUp || !!tooltipDown,
  });

  const supportClass = classNames("Input__support", {
    dark: mode === "dark",
    error: !!errorCaption && !clearPressed,
  });

  // eslint-disable-next-line consistent-return
  const supportText = useMemo(() => {
    if (help && !errorCaption) {
      return help;
    }
    if (errorCaption) {
      return errorCaption;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorCaption, help, error]);

  return (
    <div className={componentClass}>
      <label className={nameClass}>
        <input
          id={id}
          className={inputClass}
          placeholder={placeholder}
          {...fieldController}
          ref={(e) => {
            fieldController.ref(e);
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            inputRef.current = e;
          }}
          type={inputType}
          inputMode={inputMode}
          disabled={disabled}
          value={fieldController.value}
          data-cy={dataCy}
          // eslint-disable-next-line react/jsx-handler-names
          onChange={
            // eslint-disable-next-line no-extra-boolean-cast
            !!onChange
              ? (e: any) => {
                  handleChange(e);
                  setClearPressed(false);
                }
              : (e) => {
                  fieldController.onChange(e);
                  setClearPressed(false);
                }
          }
        />
        {suffix && <div className={suffixClass}>{suffix}</div>}
        {disabled && !editable && !copied && (
          <div className="Input__locked">
            <Locked />
          </div>
        )}
        {disabled && editable && (
          <div className="Input__disabled">
            <Edit />
          </div>
        )}
        {disabled && copied && (
          <button
            type="button"
            className="InputMask__copied"
            onClick={handleCopy}
          ></button>
        )}
        {tooltipUp && (
          <>
            <button
              className="Input__tooltip"
              // eslint-disable-next-line react/jsx-handler-names
              onClick={tooltipTrigger}
              type="button"
              ref={tooltipTriggerRef}
            />
            <Tooltip
              triggerRef={tooltipTriggerRef}
              isVisible={isTooltipVisible}
              ref={tooltipRef}
              position="up"
            >
              {tooltipUp}
            </Tooltip>
          </>
        )}
        {tooltipDown && (
          <>
            <button
              className="Input__tooltip"
              // eslint-disable-next-line react/jsx-handler-names
              onClick={tooltipTrigger}
              type="button"
              ref={tooltipTriggerRef}
            />
            <Tooltip
              triggerRef={tooltipTriggerRef}
              isVisible={isTooltipVisible}
              ref={tooltipRef}
              position="down"
            >
              {tooltipDown}
            </Tooltip>
          </>
        )}
        {type === "password" && fieldController.value.length && !disabled ? (
          <button type="button" className={eyeClass} onClick={handleShowPass}>
            {inputType === "password" ? <EyeHideIcon /> : <EyeOpenIcon />}
          </button>
        ) : null}
        {!!errorCaption && !disabled && <div className={attentionClass}></div>}
        <div className={borderClass}>
          <div className="Input__border_left"></div>
          {label && (
            <div className="Input__border_center">
              <span className="Input__label">{label}</span>
            </div>
          )}
          <div className="Input__border_right"></div>
        </div>
        {fieldController.value.length && !disabled && editable ? (
          <button
            type="button"
            className={clearClass}
            onClick={handleReset}
          ></button>
        ) : null}
      </label>
      {supportText && !disabled && (
        <div className={supportClass} ref={supportTextRef}>
          {supportText}
        </div>
      )}
    </div>
  );
};

export default memo(Input);
